<template>
  <!--
    把一条评论私信分享给好友（App 端评论菜单里的「分享给好友」）。
    好友列表直接复用视频播放器那个带搜索、能翻页的 VideoFriendPicker，发出去的卡片
    和 App 端是同一种（见 commentActions.buildCommentShareContent），两端的私信页都认得。
  -->
  <el-dialog
    title="分享给好友"
    :visible.sync="dialogVisible"
    width="420px"
    append-to-body
    :lock-scroll="false"
    custom-class="comment-share-dialog"
  >
    <div v-if="quote" class="comment-share-quote">
      <span class="comment-share-quote__name">{{ authorName }}：</span>{{ quote }}
    </div>
    <div class="comment-share-friends">
      <!-- v-if：每次打开都重建，好友列表和搜索词从头开始，和视频播放器里的用法一致 -->
      <video-friend-picker
        v-if="dialogVisible"
        :sending-key="sendingKey"
        :shared-keys="sharedKeys"
        @share="shareTo"
      />
    </div>
  </el-dialog>
</template>

<script>
import { messageFetch } from '@/api'
import VideoFriendPicker from '@/components/video/VideoFriendPicker.vue'
import { buildCommentShareContent, commentSummary } from './commentActions'

// 分享成功后停一下再收起，让用户看到按钮变成「已分享」（视频播放器同款）
const SHARED_CLOSE_DELAY_MS = 800

export default {
  name: 'CommentShareDialog',
  components: { VideoFriendPicker },
  props: {
    visible: { type: Boolean, default: false },
    comment: { type: Object, default: null },
    articleId: { type: [String, Number], default: '' }
  },
  data () {
    return {
      sendingKey: '',
      sharedKeys: []
    }
  },
  computed: {
    dialogVisible: {
      get () { return this.visible },
      set (val) { this.$emit('update:visible', val) }
    },
    authorName () {
      return (this.comment && this.comment.nickname) || '评论'
    },
    quote () {
      return commentSummary(this.comment, 60)
    }
  },
  watch: {
    visible (val) {
      if (val) {
        // 换了一条评论再分享，「已分享」的状态不能带过来
        this.sharedKeys = []
        this.sendingKey = ''
      } else {
        clearTimeout(this.closeTimer)
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.closeTimer)
  },
  methods: {
    async shareTo (friend) {
      const comment = this.comment
      if (!comment || !comment.id || !friend || !friend.key) return
      if (this.sendingKey || this.sharedKeys.indexOf(friend.key) !== -1) return

      this.sendingKey = friend.key
      try {
        await messageFetch.sendChatMessage({
          toUserId: friend.userId,
          content: buildCommentShareContent(comment, this.articleId),
          imageUrls: [],
          messageType: 5
        })
        this.sharedKeys = this.sharedKeys.concat(friend.key)
        this.$toast.success(`已分享给 ${friend.nickname || '好友'}`)
        clearTimeout(this.closeTimer)
        this.closeTimer = setTimeout(() => {
          this.dialogVisible = false
        }, SHARED_CLOSE_DELAY_MS)
      } catch (e) {
        // 失败提示已由请求拦截器统一给出（被拉黑、对方关闭私信等原因由服务端说明）
      } finally {
        this.sendingKey = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.comment-share-quote {
  margin: 0 0 4px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #f5f7fa;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &__name {
    color: #303133;
  }
}

/* VideoFriendPicker 是按「撑满父容器」写的（height: 100% + 内部滚动），这里给它一个确定的高度 */
.comment-share-friends {
  height: 52vh;
  max-height: 460px;
  min-height: 280px;
  margin: 0 -16px;
}
</style>

<style>
.comment-share-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.comment-share-dialog .el-dialog__header {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
}
/* 移动端的宽度和内边距由 assets/styles/mobile.scss 的「el-dialog 通用适配」统一处理 */
.comment-share-dialog .el-dialog__body {
  padding: 12px 16px 8px;
}
</style>
